import { useState } from "react";
import { Product } from "@ludens-reklame/rubics-v3-sdk/product/types";
import { useCart } from "@ludens-reklame/rubics-v3-react/cart/hooks";
import { useModal } from "@ludens-reklame/react-modal";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import {
  Constrainer,
  VerticalSpacer,
  HorizontalSpacer,
} from "../../internal-components/Layout.js";
import {
  Card,
  ComponentTitle,
  Content,
  ContentListHeading,
  ContentListWrapper,
  Cta,
  Grid,
  GridItem,
  List,
  ListItem,
  ListItemIcon,
  LogoGrid,
  LogoGridItem,
  Title,
  TopTitle,
} from "./styles.js";
import { RichText } from "../../internal-components/RichText.js";
import { ProductPrice } from "../../internal-components/product/ProductPrice.js";
import { Button } from "../../internal-components/Button.js";
import { ProductProps } from "../../types/product.js";
import { Busy } from "../../internal-components/Busy.js";
import { AddToCartModal } from "../Product/AddToCartModal.js";
import { chevron, check, destroy } from "../../icons/index.js";

interface ContentItem {
  text?: string;
  image?: Image;
  unavailable?: boolean;
}

interface ContentListItem {
  title?: string;
  type?: "spesifikasjoner" | "logo-grid";
  removeListStyle?: boolean;
  defaultOpen?: boolean;
  content?: ContentItem[];
}

interface Card {
  product?: Product<ProductProps>;
  title?: string;
  text?: string;
  topLine?: string;
  priceDescription?: string;
  buttonText?: string;
  contentList?: ContentListItem[];
}

interface Props {
  title?: string;
  cards?: Card[];
}

export const ProductComparison: React.FC<Props> = ({ title, cards = [] }) => {
  const { addItem, networking } = useCart();
  const { spawnModal } = useModal();
  const someHasTopTitle = cards.some((c) => !!c.topLine);

  return (
    <VerticalSpacer>
      <Constrainer>
        <HorizontalSpacer>
          {title && (
            <ComponentTitle>
              <RichText dangerouslySetInnerHTML={{ __html: title }} />
            </ComponentTitle>
          )}
          <Busy $busy={networking}>
            <Grid>
              {cards.map((c, k) => {
                const title = c.title || c.product?.name;
                const text = c.text || c.product?.description;

                return (
                  <GridItem
                    key={k}
                    $hasTopTitle={!!c.topLine}
                    $someHasTopTitle={someHasTopTitle}
                  >
                    {c.topLine && <TopTitle>{c.topLine}</TopTitle>}
                    <Card>
                      {title && <Title>{title}</Title>}
                      <Content>
                        {text && (
                          <RichText
                            dangerouslySetInnerHTML={{ __html: text }}
                          />
                        )}
                        {c.product && (
                          <Cta>
                            <div className="h6">
                              <ProductPrice
                                product={c.product}
                                priceClass="h2"
                              />
                            </div>
                            {c.priceDescription && (
                              <RichText
                                dangerouslySetInnerHTML={{
                                  __html: c.priceDescription,
                                }}
                              />
                            )}
                            <Button
                              type="button"
                              $variant="primary"
                              disabled={networking}
                              onClick={async () => {
                                const item = await addItem({
                                  product: c.product!._id,
                                  quantity: 1,
                                });

                                if (item) {
                                  spawnModal(<AddToCartModal item={item} />);
                                }
                              }}
                            >
                              {c.buttonText ||
                                c.product.props.addToCartText ||
                                "Bestill"}
                            </Button>
                          </Cta>
                        )}
                      </Content>
                      {(c.contentList || []).map((c, k) => (
                        <ContentList key={k} item={c} />
                      ))}
                    </Card>
                  </GridItem>
                );
              })}
            </Grid>
          </Busy>
        </HorizontalSpacer>
      </Constrainer>
    </VerticalSpacer>
  );
};

interface ContentListProps {
  item: ContentListItem;
}

const ContentList: React.FC<ContentListProps> = ({ item }) => {
  const [expanded, setExpanded] = useState<boolean>(item.defaultOpen || false);

  return (
    <ContentListWrapper>
      <ContentListHeading
        href="#"
        aria-label={`${expanded ? "Minimer" : "Ekspander"} ${
          item.title || "element"
        }`}
        onClick={(e) => {
          e.preventDefault();
          setExpanded(!expanded);
        }}
        $expanded={expanded}
      >
        {item.title} {chevron}
      </ContentListHeading>
      {expanded && <ContentFeatures item={item} />}
    </ContentListWrapper>
  );
};

interface ContentFeaturesProps {
  item: ContentListItem;
}

const ContentFeatures: React.FC<ContentFeaturesProps> = ({ item }) => {
  if (item.type === "logo-grid") {
    return (
      <LogoGrid>
        {(item.content || []).map((c, k) => (
          <LogoGridItem key={k}>
            {c.image && (
              <img
                src={transformImageUrl(c.image, {
                  width: 140,
                  height: 140,
                  fit: "contain",
                })}
                alt={c.image.altText}
                width={70}
                height={70}
                loading="lazy"
              />
            )}
            {c.text && (
              <RichText
                className="_rt"
                dangerouslySetInnerHTML={{ __html: c.text }}
              />
            )}
          </LogoGridItem>
        ))}
      </LogoGrid>
    );
  }

  return (
    <List removeListStyle={item.removeListStyle}>
      {(item.content || []).map((c, k) => (
        <ListItem key={k} disabled={c.unavailable}>
          {!item.removeListStyle && (
            <ListItemIcon>{c.unavailable ? destroy : check}</ListItemIcon>
          )}
          {c.text && (
            <RichText
              className="_rt"
              dangerouslySetInnerHTML={{ __html: c.text }}
            />
          )}
        </ListItem>
      ))}
    </List>
  );
};
